import axios from 'axios';
import config from '../config/environment';
import { recaptchaService } from '../utils/recaptchaService';
import { PRICING_CACHE_PREFIX } from './pricingService';

// Platform site pricing interfaces (matches actual API response)
export interface PlatformSitePricingPlan {
  id: number;
  serviceId: number;
  serviceName: string;
  planCode: string;
  cityCode: string;
  cityName: string;
  basePrice: number;
  gstPercentage: number;
  gstAmount: number;
  totalAmount: number;
  status: string;
  message: string;
  timestamp: string;
}

// The API returns an array of plans directly, not a wrapped object
export type PlatformSitePricingResponse = PlatformSitePricingPlan[];

// Helper interface for internal processing
export interface ProcessedPlatformPricing {
  serviceId: number;
  serviceName: string;
  cityCode: string;
  locationType: 'PLATFORM_SITE';
  currency: string;
  plans: PlatformSitePricingPlan[];
  isAvailable: boolean;
  message?: string;
}

// Create axios instance for platform site pricing API calls
// NOTE: No withCredentials here - this hits a public endpoint (/public/pricing/...)
// Using withCredentials on public endpoints forces credentialed CORS which requires
// the backend to use an exact allowed origin (not wildcard *), causing failures on Azure.
const platformPricingApi = axios.create({
  baseURL: config.authServerUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for logging
platformPricingApi.interceptors.request.use(
  (config) => {
    console.log('🔐 Platform pricing API request:', {
      url: config.url,
      params: config.params,
      headers: config.headers
    });
    
    return config;
  },
  (error) => {
    console.error('❌ Platform pricing request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
platformPricingApi.interceptors.response.use(
  (response) => {
    console.log('✅ Platform pricing API response:', {
      status: response.status,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('❌ Platform pricing API error:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      url: error.config?.url
    });
    return Promise.reject(error);
  }
);

export const platformSitePricingService = {
  /**
   * Initialize reCAPTCHA if not already loaded
   */
  initRecaptcha: async (): Promise<void> => {
    try {
      await recaptchaService.loadRecaptcha();
      console.log('✅ reCAPTCHA initialized for platform pricing');
    } catch (error) {
      console.warn('⚠️ reCAPTCHA initialization failed, continuing without it:', error);
    }
  },

  /**
   * Read platform site pricing from the CityPricingContext localStorage cache.
   * CityPricingContext pre-warms the cache key in the background on every page
   * load, so this is available immediately without an API call.
   * Returns null if cache is absent or service not found.
   */
  tryReadFromLocalStorageCache: (serviceId: number): ProcessedPlatformPricing | null => {
    try {
      const raw = localStorage.getItem(`${PRICING_CACHE_PREFIX}PLATFORM_SITE`);
      if (!raw) return null;
      const cached = JSON.parse(raw);
      const service = cached?.services?.find((s: any) => s.serviceId === serviceId);
      if (!service || !service.plans?.length) return null;

      const plans: PlatformSitePricingPlan[] = service.plans.map((p: any) => ({
        id: 0,
        serviceId,
        serviceName: service.serviceName,
        planCode: p.planCode,
        cityCode: 'PLATFORM_SITE',
        cityName: 'Kashi – Antaryatra Site',
        basePrice: p.basePrice,
        gstPercentage: 0,
        gstAmount: p.gstAmount,
        totalAmount: p.totalAmount,
        status: 'SUCCESS',
        message: '',
        timestamp: ''
      }));

      console.log(`⚡ Platform pricing served from localStorage cache for service ${serviceId}`);
      return {
        serviceId,
        serviceName: service.serviceName,
        cityCode: 'PLATFORM_SITE',
        locationType: 'PLATFORM_SITE',
        currency: 'INR',
        plans,
        isAvailable: true
      };
    } catch {
      return null;
    }
  },

  /**
   * Fetch platform site pricing for a specific service
   * @param serviceId - The service ID
   * @param recaptchaToken - Optional reCAPTCHA token, if not provided, will generate one
   * @returns Promise with platform site pricing data
   */
  getPlatformSitePricing: async (
    serviceId: number, 
    recaptchaToken?: string
  ): Promise<ProcessedPlatformPricing> => {
    // ── Cache-first: use pre-warmed localStorage data when available ────────
    const cached = platformSitePricingService.tryReadFromLocalStorageCache(serviceId);
    if (cached) return cached;
    // ────────────────────────────────────────────────────────────────────────
    try {
      console.log(`🏹️ Fetching platform site pricing for service ${serviceId} (cache miss)`);
      
      // Get reCAPTCHA token if not provided
      let token = recaptchaToken;
      if (!token) {
        try {
          token = await recaptchaService.getToken('platform_pricing');
          if (token) {
            console.log('✅ reCAPTCHA token generated for platform pricing');
          } else {
            console.warn('⚠️ Empty reCAPTCHA token returned, proceeding with empty token');
            token = '';
          }
        } catch (recaptchaError) {
          console.warn('⚠️ reCAPTCHA token generation failed, proceeding with empty token:', recaptchaError);
          token = '';
        }
      }
      
      const response = await platformPricingApi.get<PlatformSitePricingResponse>(
        `/public/pricing/service/${serviceId}/city/PLATFORM_SITE`,
        {
          params: {
            recaptchaToken: token
          }
        }
      );
      
      console.log('🔍 Platform site pricing raw response:', response.data);
      
      // API returns array directly, so process it
      const plansArray = response.data;
      
      console.log('🔍 Processing API response:', {
        responseType: typeof plansArray,
        isArray: Array.isArray(plansArray),
        length: plansArray?.length,
        firstItem: plansArray?.[0]
      });
      
      if (!Array.isArray(plansArray)) {
        console.error('❌ API response is not an array:', plansArray);
        throw new Error('Invalid API response format - expected array');
      }
      
      if (plansArray.length === 0) {
        console.error('❌ API returned empty plans array');
        throw new Error('No pricing plans available for platform site');
      }
      
      // Check if all plans have success status
      const successPlans = plansArray.filter(plan => plan.status === 'SUCCESS');
      console.log(`📋 Plans summary: ${plansArray.length} total, ${successPlans.length} successful`);
      
      if (successPlans.length === 0) {
        console.error('❌ No successful plans found in response');
        console.error('Plan statuses:', plansArray.map(p => ({ planCode: p.planCode, status: p.status })));
        throw new Error('No valid pricing plans available for platform site');
      }
      
      // Get first plan for service metadata
      const firstPlan = successPlans[0];
      
      const processedResponse: ProcessedPlatformPricing = {
        serviceId: firstPlan.serviceId,
        serviceName: firstPlan.serviceName,
        cityCode: firstPlan.cityCode,
        locationType: 'PLATFORM_SITE',
        currency: 'INR',
        plans: successPlans,
        isAvailable: true,
        message: firstPlan.message
      };
      
      console.log('✅ Platform site pricing processed successfully:', {
        serviceId: processedResponse.serviceId,
        serviceName: processedResponse.serviceName,
        isAvailable: processedResponse.isAvailable,
        plansCount: processedResponse.plans.length
      });
      
      return processedResponse;
    } catch (error) {
      console.error('❌ Error fetching platform site pricing:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const errorMessage = error.response.data?.message || error.response.data?.error || 'Failed to fetch platform site pricing';
          throw new Error(`Failed to fetch platform site pricing: ${errorMessage}`);
        } else if (error.request) {
          throw new Error('Network error: Could not reach pricing server');
        }
      }
      
      throw error;
    }
  },

  /**
   * Compare user site vs platform site pricing
   * @param serviceId - The service ID
   * @param userSitePrice - Current user site price
   * @param planName - Selected plan name
   * @param recaptchaToken - Optional reCAPTCHA token
   * @returns Promise with pricing comparison data
   */
  comparePricing: async (
    serviceId: number, 
    userSitePrice: number, 
    planName: string,
    recaptchaToken?: string
  ) => {
    try {
      console.log(`🔍 Starting price comparison for service ${serviceId}, plan: ${planName}, user price: ₹${userSitePrice}`);
      
      const platformPricing: ProcessedPlatformPricing = await platformSitePricingService.getPlatformSitePricing(serviceId, recaptchaToken);
      
      console.log('🏛️ Platform pricing response received:', {
        isAvailable: platformPricing.isAvailable,
        plansCount: platformPricing.plans?.length || 0,
        serviceName: platformPricing.serviceName,
        plans: platformPricing.plans?.map(p => ({ planCode: p.planCode, totalAmount: p.totalAmount, status: p.status }))
      });
      
      if (!platformPricing.isAvailable) {
        console.error('❌ Platform site service marked as not available');
        throw new Error('Platform site service is not available for this service');
      }
      
      if (!platformPricing.plans || platformPricing.plans.length === 0) {
        console.error('❌ No pricing plans available in response');
        throw new Error('No pricing plans available for platform site');
      }
      
      // Find the matching plan by plan code
      const normalizedPlanName = planName.toLowerCase().trim();
      console.log(`🔍 Looking for plan matching: "${normalizedPlanName}"`);
      
      let matchingPlan = platformPricing.plans.find((plan: PlatformSitePricingPlan) => {
        const planCode = plan.planCode.toLowerCase();
        const isMatch = planCode === normalizedPlanName || 
                       planCode.includes(normalizedPlanName) ||
                       normalizedPlanName.includes(planCode);
        
        console.log(`  - Plan: ${plan.planCode} (${planCode}) -> Match: ${isMatch}`);
        return isMatch;
      });
      
      if (!matchingPlan) {
        console.warn(`⚠️ No matching plan found for "${planName}", using first available plan`);
        console.log(`Available plans:`, platformPricing.plans.map(p => p.planCode));
        matchingPlan = platformPricing.plans[0];
        if (!matchingPlan) {
          throw new Error('No pricing plans available for platform site');
        }
      }
      
      console.log(`📊 Using platform plan: ${matchingPlan.planCode} (₹${matchingPlan.totalAmount}) vs user price: ₹${userSitePrice}`);
      
      const result = {
        platformSitePrice: matchingPlan.totalAmount,
        userSitePrice,
        priceDifference: matchingPlan.totalAmount - userSitePrice,
        platformPlan: matchingPlan,
        serviceName: platformPricing.serviceName
      };
      
      console.log('✅ Price comparison completed:', result);
      return result;
      
    } catch (error) {
      console.error('❌ Error comparing pricing:', error);
      throw error;
    }
  },

  /**
   * Get a fresh reCAPTCHA token for platform pricing
   * @param action - The action name for reCAPTCHA v3
   * @returns Promise<string> reCAPTCHA token
   */
  getRecaptchaToken: async (action = 'platform_pricing'): Promise<string> => {
    try {
      const token = await recaptchaService.getToken(action);
      return token || '';
    } catch (error) {
      console.warn('⚠️ Failed to get reCAPTCHA token:', error);
      return '';
    }
  }
};

export default platformSitePricingService;